/* eslint-disable react/display-name */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useEffect } from 'react';
import { FormikProvider } from 'formik';
import { Button, AutoComplete, Checkbox } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import {
  Table, AddRowButton, Form, Input, RemoveRowButton,
} from 'formik-antd';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import isEqual from 'lodash.isequal';
import { IColumn } from '../../../store/modules/report/i-column';
import SecondaryButton from '../../lib/secondary-button';
import { ISecondStep } from './interfaces';
import './create-report.less';
import { useActions } from '../../providers/actions-provider/actions.context';
import { useStore } from '../../../store';

const stringArrayToAutocompleteOptions = (stringArray: string[]) => (
  (stringArray || []).map((item) => ({ value: item, label: item }))
);

const SecondStepTable = ({
  collection,
  stepHandler,
  fieldOrder,
  setFieldOrder,
  formikForSecondStep,
  initialField,
}: ISecondStep): JSX.Element => {
  const { reportActions } = useActions();
  const { allColumnsOfReport } = useStore();
  const [editField, setEditField] = useState(1);
  const [editFieldValues, setEditFieldValues] = useState(initialField);
  const [fieldOptions, setFieldOptions] = useState<{ value: string, label: string }[]>([]);
  const {
    setFieldValue: setFieldValueForSecondStep,
    values: secondStepValues,
  } = formikForSecondStep;

  // DND wrappers, etc.
  const [sorted, setSorted] = useState(secondStepValues.fieldsTable);
  interface IIndexs {
    oldIndex: number;
    newIndex: number;
  }

  useEffect(() => {
    reportActions.getAllColumnsForReport(collection);
  }, [collection]);

  const DragHandle = SortableHandle(() => (
    <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
  ));
  const SortItem = SortableElement((props: any) => <tr {...props} />);
  const SortContainer = SortableContainer((props: any) => <tbody {...props} />);
  const onSortEnd = ({ oldIndex, newIndex }: IIndexs) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMove(([] as IColumn[]).concat(sorted), oldIndex, newIndex)
        .filter((el) => !!el);
      const segment = newData.map((el, index) => {
        if (el.order !== index + 1) {
          return {
            ...el,
            order: index + 1,
          };
        }
        return { ...el };
      });
      secondStepValues.fieldsTable = segment;
      setSorted(segment);
    }
  };
  const onFieldSearch = (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      const opts = (allColumnsOfReport.data || []).filter(
        (column) => (
          column.toLowerCase().includes(trimmedSearchText.toLowerCase())),
      );
      setFieldOptions(stringArrayToAutocompleteOptions(opts));
    } else {
      setFieldOptions(stringArrayToAutocompleteOptions(allColumnsOfReport.data));
    }
  };
  const DraggableBodyRow = useMemo(() => function dragRow({ ...restProps }) {
    const index = sorted.findIndex(
      (x) => x.order === restProps['data-row-key'],
    );
    return <SortItem index={index} {...restProps} />;
  }, [sorted]);
  const DraggableContainer = useMemo(() => function dragContainer(props: JSX.IntrinsicAttributes &
  JSX.IntrinsicClassAttributes<React.Component<any, any, any>> & Readonly<any>
  & Readonly<{ children?: React.ReactNode; }>): any {
    return (
      <SortContainer
        useDragHandle
        helperClass="row-dragging"
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  }, [sorted]);
  const components = {
    body: {
      wrapper: DraggableContainer,
      row: DraggableBodyRow,
    },
  };

  const columns = [
    {
      dataIndex: 'sort',
      width: '1%',
      className: 'drag-visible',
      render: () => (!editField && <DragHandle />),
    },
    {
      title: 'Header',
      dataIndex: 'header',
      key: 'header',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <Input
          type="text"
          name={`fieldsTable.${index}.header`}
          className="field__row-input"
        />
      ) : (
        text
      )),
    },
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: '25%',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <AutoComplete
          dropdownClassName="table-field-options"
          value={text}
          options={fieldOptions}
          style={{ width: '100%' }}
          onSearch={onFieldSearch}
          onChange={(value:string) => {
            setFieldValueForSecondStep(`fieldsTable.${index}.field`, value);
          }}
          onFocus={() => {
            setFieldOptions(stringArrayToAutocompleteOptions(allColumnsOfReport.data));
          }}
        />
      ) : (
        text
      )),
    },
    {
      title: () => (editField !== 0 ? (
        <>
          <div>Format</div>
          <div className="optional-field">Optional</div>
        </>
      ) : 'Format'
      ),
      dataIndex: 'format',
      key: 'format',
      width: '30%',
      render: (text: string, record: IColumn, index: number) => (record.order === editField ? (
        <Input name={`fieldsTable.${index}.format`} type="text" className="field__row-input" />
      ) : (
        text
      )),
    },
    {
      title: 'Hidden',
      dataIndex: 'hidden',
      key: 'hidden',
      width: '8%',
      render: (text: string, record: IColumn, index: number) => (
        <Checkbox
          checked={!!record.hidden}
          disabled={record.order !== editField}
          onChange={(e) => {
            setFieldValueForSecondStep(`fieldsTable.${index}.hidden`, e.target.checked ? 1 : 0);
          }}
        />
      ),
    },
    {
      dataIndex: 'delete',
      key: 'delete',
      width: '5%',
      render: (text: string, record: IColumn, index: number) => (editField === record.order ? (
        <SecondaryButton
          type="button"
          className="ant-btn--small ant-btn-ghost button-cancel"
          onClick={() => {
            if (isEqual(record, initialField)) {
              secondStepValues.fieldsTable = secondStepValues.fieldsTable.slice(0, -1);
              setEditField(0);
            } else {
              const prevFields = secondStepValues.fieldsTable.map((el) => {
                if (el.order === editFieldValues.order) {
                  return editFieldValues;
                }
                return el;
              });
              secondStepValues.fieldsTable = prevFields;
              setEditField(0);
            }
          }}
        >
          cancel
        </SecondaryButton>
      ) : (
        <RemoveRowButton
          name="fieldsTable"
          index={index}
          className="button-cancel ant-btn--small ant-btn-ghost"
        >
          delete
        </RemoveRowButton>
      )),
    },
    {
      dataIndex: 'edit',
      key: 'edit',
      width: '5%',
      render: (text: string, record: IColumn) => (editField === record.order ? (
        <SecondaryButton
          type="submit"
          className="button-save ant-btn--small"
          disabled={!(record.header && record.field)}
          onClick={() => {
            setEditField(0);
            setSorted(secondStepValues.fieldsTable);
          }}
        >
          save
        </SecondaryButton>
      ) : (
        <SecondaryButton
          type="button"
          className="button-edit ant-btn--small"
          onClick={() => {
            setEditFieldValues(record);
            setEditField(record.order);
          }}
        >
          edit
        </SecondaryButton>
      )),
    },
  ];

  return (
    <FormikProvider value={formikForSecondStep}>
      <Form>
        <div className="create-column__wrapper">
          <p className="create-column__title" />
          <AddRowButton
            name="fieldsTable"
            className="create-column__new"
            key={fieldOrder}
            disabled={editField !== 0}
            createNewRow={() => {
              setFieldOrder(fieldOrder + 1);
              setEditField(fieldOrder + 1);
              return {
                report_id: 0,
                order: fieldOrder + 1,
                field: '',
                header: '',
                format: '',
                align: '',
                width: '',
                hidden: 0,
              };
            }}
          >
            New Field
          </AddRowButton>
        </div>
        <Table
          name="fieldsTable"
          // size="small"
          className="fields__table"
          columns={columns}
          key="index"
          rowKey={(record) => record.order}
          pagination={false}
          components={components}
        />
        <div className="fields-table__button-wrapper">
          <Button
            htmlType="reset"
            type="default"
            style={{ width: 280 }}
            className="fields-table__button-cancel"
            onClick={() => {
              stepHandler(0);
            }}
          >
            cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            style={{ width: 280 }}
            className="fields-table__button-next"
            disabled={editField !== 0}
            onClick={() => {
              stepHandler(2);
            }}
          >
            next
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
};

SecondStepTable.displayName = 'SecondStepTable';
export default SecondStepTable;
